// src/utils/sweepFeatures.js
// Shape-feature extraction from FDC221x capacitive-sensor IDRIVE sweep (32 points).
// Each sweep point measures resonant frequency at a different drive current.
// Treating the curve as a distribution in log(IDRIVE) space yields statistical
// moments (centroid, σ, skewness, kurtosis) and geometric features that
// correlate with medium properties (conductivity, concentration, temperature).

/**
 * 32 IDRIVE values (µA) from firmware enum fdc221x_drive_current_t.
 * 5-bit register, geometric progression (~16 % per step).
 */
export const IDRIVE_UA = [
    16, 18, 21, 25, 28, 33, 38, 44,
    52, 60, 69, 81, 93, 108, 126, 146,
    169, 196, 228, 264, 307, 356, 413, 479,
    555, 644, 747, 867, 1006, 1167, 1354, 1571,
];

/** Precomputed log10(IDRIVE) — used as x-axis for moment calculations. */
export const LOG_IDRIVE = IDRIVE_UA.map((v) => Math.log10(v));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Trapezoidal numerical integration of y over x. */
function trapezoid(y, x) {
    let sum = 0;
    for (let i = 1; i < x.length; i++) {
        sum += 0.5 * (y[i] + y[i - 1]) * (x[i] - x[i - 1]);
    }
    return sum;
}

/** Simple linear regression slope for arrays x, y of equal length. */
function linearSlope(x, y) {
    const n = x.length;
    if (n < 2) return 0;
    let sx = 0, sy = 0, sxy = 0, sxx = 0;
    for (let i = 0; i < n; i++) {
        sx += x[i];
        sy += y[i];
        sxy += x[i] * y[i];
        sxx += x[i] * x[i];
    }
    const denom = n * sxx - sx * sx;
    if (denom === 0) return 0;
    return (n * sxy - sx * sy) / denom;
}

// ---------------------------------------------------------------------------
// Main extraction
// ---------------------------------------------------------------------------

/**
 * Extract shape features from a single-channel sweep.
 *
 * @param {number[][]} sweepArray  – 32-element array of [ch0, ch1] pairs
 * @param {number}     chIdx      – 0 or 1 (channel index)
 * @returns {object|null} features or null if data is invalid
 */
export function extractSweepFeatures(sweepArray, chIdx) {
    if (!Array.isArray(sweepArray) || sweepArray.length !== 32) return null;

    const freq = sweepArray.map((pair) => pair?.[chIdx]);
    if (freq.some((v) => v == null || !isFinite(v))) return null;

    const x = LOG_IDRIVE; // log10(IDRIVE)
    const n = freq.length;

    // --- Peak ---
    let peakIdx = 0;
    for (let i = 1; i < n; i++) {
        if (freq[i] > freq[peakIdx]) peakIdx = i;
    }
    const peakFreq = freq[peakIdx];
    const fMin = Math.min(...freq);
    const dynamicRange = peakFreq - fMin;

    // --- Baseline subtraction ---
    const yShifted = freq.map((f) => f - fMin);

    // --- Area (trapezoidal integration) ---
    const area = trapezoid(yShifted, x);
    if (area <= 0) {
        // Flat curve — moments are undefined
        return {
            peakFreq, peakIndex: peakIdx, dynamicRange,
            area: 0, centroid: null, sigma: null,
            skewness: null, kurtosis: null, asymRatio: null,
            ascentSlope: null, descentSlope: null,
        };
    }

    // --- Moments ---
    const centroid = trapezoid(yShifted.map((y, i) => y * x[i]), x) / area;

    const m2 = trapezoid(yShifted.map((y, i) => y * (x[i] - centroid) ** 2), x) / area;
    const sigma = Math.sqrt(m2);

    let skewness = null;
    let kurtosis = null;
    if (sigma > 0) {
        const m3 = trapezoid(yShifted.map((y, i) => y * (x[i] - centroid) ** 3), x) / area;
        const m4 = trapezoid(yShifted.map((y, i) => y * (x[i] - centroid) ** 4), x) / area;
        skewness = m3 / (sigma ** 3);
        kurtosis = m4 / (sigma ** 4) - 3; // excess kurtosis
    }

    // --- Asymmetry ratio (left area / right area around peak) ---
    let asymRatio = null;
    if (peakIdx > 0 && peakIdx < n - 1) {
        const leftY = yShifted.slice(0, peakIdx + 1);
        const leftX = x.slice(0, peakIdx + 1);
        const rightY = yShifted.slice(peakIdx);
        const rightX = x.slice(peakIdx);
        const areaLeft = trapezoid(leftY, leftX);
        const areaRight = trapezoid(rightY, rightX);
        asymRatio = areaRight > 0 ? areaLeft / areaRight : null;
    }

    // --- Slopes ---
    const ascStart = Math.max(0, peakIdx - 5);
    const ascX = x.slice(ascStart, peakIdx + 1);
    const ascY = freq.slice(ascStart, peakIdx + 1);
    const ascentSlope = ascX.length >= 2 ? linearSlope(ascX, ascY) : null;

    const descEnd = Math.min(n, peakIdx + 11);
    const descX = x.slice(peakIdx, descEnd);
    const descY = freq.slice(peakIdx, descEnd);
    const descentSlope = descX.length >= 2 ? linearSlope(descX, descY) : null;

    return {
        peakFreq,
        peakIndex: peakIdx,
        dynamicRange,
        area,
        centroid,
        sigma,
        skewness,
        kurtosis,
        asymRatio,
        ascentSlope,
        descentSlope,
    };
}

/**
 * Extract sweep features for both channels from a device telemetry object.
 *
 * @param {object} device – telemetry object (must contain capacitiveSensor.sweep)
 * @returns {{ channel0: object|null, channel1: object|null }}
 */
export function extractSweepFeaturesForDevice(device) {
    const sweep = device?.capacitiveSensor?.sweep;
    return {
        channel0: extractSweepFeatures(sweep, 0),
        channel1: extractSweepFeatures(sweep, 1),
    };
}
